/**
 * bucket-manager brand mark + UI primitives.
 * Ported from bucket-manager-design/brand.jsx.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react';

export type BadgeVariant = 'neutral' | 'success' | 'warn' | 'danger' | 'info' | 'accent';

export type BannerVariant = 'info' | 'warn' | 'danger';

interface BaseProps {
  style?: CSSProperties;
  className?: string;
}

/* ─── Brand mark ─────────────────────────────────────────────────────── */

interface LogoProps extends BaseProps {
  size?: number;
  accent?: string;
  alive?: boolean;
}

export function Logo({ size = 48, accent, alive = false, className = '', style }: LogoProps) {
  const accentFill = accent || 'var(--bm-accent)';
  return (
    <span
      className={`bm-logo ${alive ? 'bm-logo-alive' : ''} ${className}`.trim()}
      style={{ width: size, height: size, display: 'inline-block', ...style }}
    >
      <svg
        viewBox="0 0 32 32"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="bucket-manager"
      >
        <path d="M 5.6 10 L 16 16 L 16 28 L 5.6 22 Z" fill="var(--bm-text)" opacity="0.92" />
        <path
          d="M 22.76 12.1 L 22.76 16.3 L 26.4 14.2 L 26.4 22 L 16 28 L 16 16 Z"
          fill="var(--bm-text)"
          opacity="0.7"
        />
        <path
          d="M 16 4 L 22.76 7.9 L 19.12 10 L 22.76 12.1 L 16 16 L 5.6 10 Z"
          fill="var(--bm-text)"
          opacity="0.5"
        />
        <path
          d="M 22.76 7.9 L 26.4 10 L 22.76 12.1 L 19.12 10 Z"
          fill={accentFill}
          opacity="0.85"
        />
        <path
          d="M 19.12 10 L 22.76 12.1 L 22.76 16.3 L 19.12 14.2 Z"
          fill={accentFill}
          opacity="0.55"
        />
        <path
          d="M 22.76 12.1 L 26.4 10 L 26.4 14.2 L 22.76 16.3 Z"
          fill={accentFill}
          opacity="0.4"
        />
        <path
          d="M 16 4 L 16 16 M 16 16 L 5.6 10 M 16 16 L 26.4 14.2"
          stroke="var(--bm-bg)"
          strokeOpacity="0.35"
          strokeWidth="0.35"
          fill="none"
        />
      </svg>
    </span>
  );
}

interface BrandProps {
  size?: number;
  showWord?: boolean;
  accent?: string;
}

export function Brand({ size = 24, showWord = true, accent }: BrandProps) {
  return (
    <div className="bm-brand" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Logo size={size} accent={accent} />
      {showWord && (
        <span
          style={{
            fontFamily: 'var(--bm-font)',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '-0.2px',
            color: 'var(--bm-text)',
          }}
        >
          bucket manager
        </span>
      )}
    </div>
  );
}

/* ─── Badges / chips / kbd / banner / spinner ───────────────────────── */

interface BadgeProps extends BaseProps {
  variant?: BadgeVariant;
  children: ReactNode;
}
export function Badge({ variant = 'neutral', children, style }: BadgeProps) {
  return (
    <span className={`bm-badge bm-badge-${variant}`} style={style}>
      {children}
    </span>
  );
}

export function Pill({ children, style, ...rest }: BaseProps & { children: ReactNode }) {
  return (
    <span className="bm-chip" style={style} {...rest}>
      {children}
    </span>
  );
}

export function Kbd({ children, style }: BaseProps & { children: ReactNode }) {
  return (
    <kbd className="bm-kbd" style={style}>
      {children}
    </kbd>
  );
}

export function Mono({ children, style }: BaseProps & { children: ReactNode }) {
  return (
    <span className="mono" style={{ fontSize: '0.95em', color: 'var(--bm-text-soft)', ...style }}>
      {children}
    </span>
  );
}

interface BannerProps {
  variant?: BannerVariant;
  code?: string;
  children: ReactNode;
  action?: ReactNode;
}
export function Banner({ variant = 'danger', code, children, action }: BannerProps) {
  return (
    <div className={`bm-banner bm-banner-${variant}`}>
      <div style={{ flex: 1 }}>
        {code && (
          <code className="mono" style={{ fontSize: 12, color: 'var(--bm-text)' }}>
            {code}:
          </code>
        )}
        <span style={{ marginLeft: code ? 8 : 0 }}>{children}</span>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function Spinner({ size = 14 }: { size?: number }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        border: '1.5px solid var(--bm-text-faint)',
        borderTopColor: 'var(--bm-text)',
        animation: 'bm-spin 0.8s linear infinite',
      }}
    />
  );
}

/* ─── Icon set ──────────────────────────────────────────────────────── */

interface IconProps {
  size?: number;
  style?: CSSProperties;
}

function SvgWrap({ size = 16, children, style }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
      role="img"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

type IconFn = (p?: IconProps) => ReactElement;

export const Icon = {
  dashboard: (p) => (
    <SvgWrap {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </SvgWrap>
  ),
  accounts: (p) => (
    <SvgWrap {...p}>
      <path d="M4 7c0-1.5 3.5-3 8-3s8 1.5 8 3-3.5 3-8 3-8-1.5-8-3z" />
      <path d="M4 7v5c0 1.5 3.5 3 8 3s8-1.5 8-3V7" />
      <path d="M4 12v5c0 1.5 3.5 3 8 3s8-1.5 8-3v-5" />
    </SvgWrap>
  ),
  bucket: (p) => (
    <SvgWrap {...p}>
      <path d="M4 7h16l-1.5 12.5c-.1.8-.8 1.5-1.6 1.5H7.1c-.8 0-1.5-.6-1.6-1.5L4 7z" />
      <path d="M4 7c0-1.5 3.5-2.5 8-2.5s8 1 8 2.5" />
    </SvgWrap>
  ),
  audit: (p) => (
    <SvgWrap {...p}>
      <path d="M5 3h11l3 3v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h5" />
      <path d="M8 12h8M8 16h8M8 8h3" />
    </SvgWrap>
  ),
  settings: (p) => (
    <SvgWrap {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </SvgWrap>
  ),
  key: (p) => (
    <SvgWrap {...p}>
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M16 7l3 3M14 9l2 2" />
    </SvgWrap>
  ),
  folder: (p) => (
    <SvgWrap {...p}>
      <path d="M3 6a1 1 0 0 1 1-1h5l2 2h8a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6z" />
    </SvgWrap>
  ),
  file: (p) => (
    <SvgWrap {...p}>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8l-5-5z" />
      <path d="M14 3v5h5" />
    </SvgWrap>
  ),
  fileImage: (p) => (
    <SvgWrap {...p}>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8l-5-5z" />
      <path d="M14 3v5h5" />
      <circle cx="10" cy="13" r="1.4" />
      <path d="M19 18l-3-3-5 5" />
    </SvgWrap>
  ),
  fileCode: (p) => (
    <SvgWrap {...p}>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8l-5-5z" />
      <path d="M14 3v5h5" />
      <path d="M10 13l-2 2 2 2M14 13l2 2-2 2" />
    </SvgWrap>
  ),
  fileVideo: (p) => (
    <SvgWrap {...p}>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8l-5-5z" />
      <path d="M14 3v5h5" />
      <path d="M10 14l4 2.5L10 19v-5z" fill="currentColor" />
    </SvgWrap>
  ),
  upload: (p) => (
    <SvgWrap {...p}>
      <path d="M12 4v11M7 9l5-5 5 5" />
      <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
    </SvgWrap>
  ),
  download: (p) => (
    <SvgWrap {...p}>
      <path d="M12 4v11M7 11l5 5 5-5" />
      <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
    </SvgWrap>
  ),
  share: (p) => (
    <SvgWrap {...p}>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8.2 11l7.6-3.7M8.2 13l7.6 3.7" />
    </SvgWrap>
  ),
  trash: (p) => (
    <SvgWrap {...p}>
      <path d="M4 7h16M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
    </SvgWrap>
  ),
  edit: (p) => (
    <SvgWrap {...p}>
      <path d="M16 3l5 5L9 20H4v-5z" />
    </SvgWrap>
  ),
  plus: (p) => (
    <SvgWrap {...p}>
      <path d="M12 5v14M5 12h14" />
    </SvgWrap>
  ),
  check: (p) => (
    <SvgWrap {...p}>
      <path d="M4 12l5 5 11-11" />
    </SvgWrap>
  ),
  x: (p) => (
    <SvgWrap {...p}>
      <path d="M6 6l12 12M18 6l-12 12" />
    </SvgWrap>
  ),
  search: (p) => (
    <SvgWrap {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M16 16l4 4" />
    </SvgWrap>
  ),
  refresh: (p) => (
    <SvgWrap {...p}>
      <path d="M3 12a9 9 0 0 1 15.5-6.5L21 8M21 4v4h-4M21 12a9 9 0 0 1-15.5 6.5L3 16M3 20v-4h4" />
    </SvgWrap>
  ),
  chevronRight: (p) => (
    <SvgWrap {...p}>
      <path d="M9 5l7 7-7 7" />
    </SvgWrap>
  ),
  chevronLeft: (p) => (
    <SvgWrap {...p}>
      <path d="M15 5l-7 7 7 7" />
    </SvgWrap>
  ),
  chevronDown: (p) => (
    <SvgWrap {...p}>
      <path d="M5 9l7 7 7-7" />
    </SvgWrap>
  ),
  arrowLeft: (p) => (
    <SvgWrap {...p}>
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </SvgWrap>
  ),
  copy: (p) => (
    <SvgWrap {...p}>
      <rect x="8" y="8" width="12" height="12" rx="1.5" />
      <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
    </SvgWrap>
  ),
  externalLink: (p) => (
    <SvgWrap {...p}>
      <path d="M14 4h6v6M20 4l-9 9M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
    </SvgWrap>
  ),
  globe: (p) => (
    <SvgWrap {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" />
    </SvgWrap>
  ),
  user: (p) => (
    <SvgWrap {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </SvgWrap>
  ),
  sun: (p) => (
    <SvgWrap {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </SvgWrap>
  ),
  moon: (p) => (
    <SvgWrap {...p}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </SvgWrap>
  ),
  list: (p) => (
    <SvgWrap {...p}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </SvgWrap>
  ),
  grid: (p) => (
    <SvgWrap {...p}>
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="4" width="7" height="7" rx="1" />
      <rect x="4" y="13" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
    </SvgWrap>
  ),
  filter: (p) => (
    <SvgWrap {...p}>
      <path d="M4 5h16l-6 8v6l-4-2v-4z" />
    </SvgWrap>
  ),
  alertCircle: (p) => (
    <SvgWrap {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6M12 17v.01" />
    </SvgWrap>
  ),
  lock: (p) => (
    <SvgWrap {...p}>
      <rect x="5" y="11" width="14" height="10" rx="1.5" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </SvgWrap>
  ),
  command: (p) => (
    <SvgWrap {...p}>
      <path d="M9 6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6z" />
    </SvgWrap>
  ),
  more: (p) => (
    <SvgWrap {...p}>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" />
    </SvgWrap>
  ),
  signout: (p) => (
    <SvgWrap {...p}>
      <path d="M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3M10 8l-4 4 4 4M6 12h11" />
    </SvgWrap>
  ),
  signin: (p) => (
    <SvgWrap {...p}>
      <path d="M9 4H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h3M14 8l4 4-4 4M18 12H7" />
    </SvgWrap>
  ),
  activity: (p) => (
    <SvgWrap {...p}>
      <path d="M3 12h4l3-8 4 16 3-8h4" />
    </SvgWrap>
  ),
} satisfies Record<string, IconFn>;

/* ─── Breadcrumb ────────────────────────────────────────────────────── */

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  onClick?: (to: string) => void;
}

export function Breadcrumb({ items, onClick }: BreadcrumbProps) {
  if (!items.length) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
    >
      {items.map((item, i) => {
        const last = i === items.length - 1;
        const interactive = item.to && !last && onClick;
        const content = interactive ? (
          <button
            type="button"
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              font: 'inherit',
              cursor: 'pointer',
              color: 'var(--bm-text-muted)',
              fontWeight: 400,
            }}
            onClick={() => onClick?.(item.to as string)}
          >
            {item.label}
          </button>
        ) : (
          <span
            style={{
              color: last ? 'var(--bm-text)' : 'var(--bm-text-muted)',
              fontWeight: last ? 500 : 400,
            }}
          >
            {item.label}
          </span>
        );
        return (
          <span
            key={item.to ?? item.label}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            {content}
            {!last && (
              <span style={{ color: 'var(--bm-text-ghost)' }}>
                <Icon.chevronRight size={12} />
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
